import React from 'react';

import {
  Select as BaseSelect,
  SelectProps as BaseSelectProps,
  StyledControlContainer as BaseStyledControlContainer,
  StyledIconsContainer as BaseStyledIconsContainer,
  StyledValueContainer as BaseStyledValueContainer,
  SIZE,
} from 'baseui/select';
import StatefulContainer from 'baseui/select/stateful-select-container';
import { StyletronComponent } from 'styletron-react';

import {
  Caption,
  getDefaultInputId,
  Label,
} from 'shared/components/ihcl/input';
import { LucideIcon } from 'shared/components/ihcl/lucideIcon';
import { Theme, useStyletron, withStyle } from 'shared/components/ihcl/styled';

import { Tag, KIND as TAG_KIND } from './tag';

export { SIZE, TAG_KIND };

const ChevronDownIcon = () => <LucideIcon name="ChevronDown" size="unit16" />;
const ChevronDownIconLarge = () => (
  <LucideIcon name="ChevronDown" size="unit24" />
);

export const StyledControlContainer = withStyle(
  BaseStyledControlContainer,
  ({ $multi, $size, $theme, $error }) => {
    let height = 'auto';
    if (!$multi) {
      height =
        $size === SIZE.compact
          ? $theme.sizing.inputCompactHeight
          : $theme.sizing.inputHeight;
    }

    const additionalStyles = $error
      ? {
          backgroundColor: $theme.colors.inputFillError,
        }
      : {};

    return {
      height,
      minHeight: $size === SIZE.large ? $theme.sizing.inputHeight : 'auto',
      ...additionalStyles,
    };
  }
);

export const StyledValueContainer = withStyle(
  BaseStyledValueContainer,
  ({ $theme, $hasLabel }: { $hasLabel: boolean; $theme: Theme }) => ({
    paddingLeft: $theme.sizing.inputPaddingLeft,
    paddingBottom: $hasLabel ? 0 : $theme.sizing.unit10,
  })
);

export const StyledCompactValueContainer: StyletronComponent<
  'div',
  { $hasLabel: boolean }
> = withStyle(BaseStyledValueContainer, ({ $theme, $hasLabel }) => ({
  fontSize: '14px',
  paddingLeft: $hasLabel
    ? $theme.sizing.unit12
    : $theme.sizing.inputCompactPadding,
  paddingRight: $hasLabel
    ? $theme.sizing.unit12
    : $theme.sizing.inputCompactPadding,
  paddingTop: $hasLabel ? $theme.sizing.unit16 : $theme.sizing.unit8,
  paddingBottom: $hasLabel ? $theme.sizing.unit2 : $theme.sizing.unit6,
}));

export type SelectProps = Pick<
  BaseSelectProps,
  | 'backspaceRemoves'
  | 'clearable'
  | 'closeOnSelect'
  | 'multi'
  | 'overrides'
  | 'placeholder'
  | 'size'
> & {
  caption?: React.ReactNode;
  className?: string;
  creatable?: boolean;
  error?: string | boolean;
  id?: string;
  inputId?: string;
  instanceId?: string;
  label?: string;
  tagColor?: string | null;
  tagKind?: keyof typeof TAG_KIND;
  tagTextColor?: string | null;
  value?: Array<any>;
  zIndex?: number;
  [key: string]: any;
};

export const Select = ({
  caption = null,
  className = '',
  clearable,
  creatable = false,
  error = false,
  id = null,
  inputId = null,
  instanceId = null,
  label = null,
  multi = false,
  overrides = null,
  size = SIZE.default,
  tagColor = null,
  tagKind = TAG_KIND.accent,
  tagTextColor = null,
  value = [],
  zIndex = null,
  ...props
}: SelectProps) => {
  const IconsContainer = BaseStyledIconsContainer;
  let ValueContainer = StyledValueContainer;
  const [, theme] = useStyletron();
  const [isFocused, setIsFocused] = React.useState(false);

  let selectInputId = inputId;
  if (!selectInputId) {
    selectInputId = getDefaultInputId();
  }

  if (size === 'compact') {
    // @ts-ignore - StyledCompactValueContainer uses $hasLabel prop internally
    ValueContainer = StyledCompactValueContainer;
  }

  const placeHolder = label ? null : 'Select...';

  let labelTop = '0';
  if (label && size === 'compact') {
    labelTop = '-1px';
  }
  if (label && !isFocused && value?.length === 0) {
    labelTop = '12px';
  }

  const popoverOverrides = zIndex
    ? {
        Popover: {
          props: {
            overrides: {
              Body: {
                style: {
                  zIndex,
                },
              },
            },
          },
        },
      }
    : {};

  return (
    <>
      <BaseSelect
        clearable={clearable}
        creatable={creatable}
        id={id}
        multi={multi}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
        placeholder={placeHolder}
        size={size}
        value={value}
        {...props}
        overrides={{
          Root: {
            props: {
              $size: size,
              className,
              style: {
                color: theme.colors.neutral,
                zIndex: zIndex || 'auto',
              },
              ...(instanceId && { 'data-instance-id': instanceId }),
            },
          },
          ClearIcon: {
            style: () =>
              size === 'compact'
                ? {
                    padding: '8px',
                    width: '32px',
                    height: '32px',
                  }
                : {
                    padding: '8px',
                    width: '40px',
                    height: '40px',
                  },
          },
          ControlContainer: {
            props: {
              $multi: multi,
              $size: size,
              $theme: theme,
              $error: error,
            },
            component: StyledControlContainer,
          },
          IconsContainer,
          Input: {
            props: {
              id: selectInputId,
              ...(instanceId && {
                'data-testid': `${instanceId}.input`,
              }),
            },
          },
          Placeholder: {
            style: {
              color: theme.colors.grayLabel,
            },
          },
          SelectArrow: {
            component:
              size === 'compact' ? ChevronDownIcon : ChevronDownIconLarge,
            style: () =>
              size === 'compact'
                ? {
                    padding: '8px 0',
                    width: '16px',
                    height: '32px',
                  }
                : {
                    padding: '8px 0',
                    width: '24px',
                    height: '40px',
                  },
          },
          ValueContainer: {
            component: ValueContainer,
            props: {
              $hasLabel: !!label,
            },
          },
          // eslint-disable-next-line react/no-unstable-nested-components
          Tag: (tagProps) => (
            <Tag
              {...tagProps}
              kind={tagKind}
              color={tagColor}
              $textColor={tagTextColor}
              variant="solid"
            />
          ),
          SingleValue: {
            style: () => ({
              display: 'flex',
              alignItems: 'center',
            }),
          },
          ...popoverOverrides,
          ...(overrides || {}),
        }}
      />
      {label && (
        // @ts-ignore
        <Label
          $isFocused={isFocused}
          $size={size}
          $top={labelTop}
          htmlFor={selectInputId}
          style={{
            maxWidth: 'calc(100% - 45px)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textWrapMode: 'nowrap',
            zIndex: zIndex ? zIndex + 1 : 'auto',
          }}
          title={label}
        >
          {label}
        </Label>
      )}
      {/* @ts-ignore */}
      {caption && <Caption $error={error}>{caption}</Caption>}
    </>
  );
};

export const StatefulSelect = (props) => (
  <StatefulContainer {...props}>
    {/* @ts-ignore */}
    {(childrenProps) => <Select {...childrenProps} />}
  </StatefulContainer>
);
